<template>
  <ul ref="vertList" class="vert-list c-availability-list">
    <li v-for="(weeks, monthStart) in availabilityList" :key="monthStart" class="vert-list__item">
      <div class="heading-bar">
        <h1 class="hdg hdg--4">
          {{ monthHeader(monthStart) }}
        </h1>
      </div>

      <div v-for="(days, weekStart) in weeks" :key="weekStart">
        <div v-if="showWeekHeader(monthStart, weekStart)" class="heading-bar-underline">
          <h2 class="section-heading">{{ weekHeader(weekStart) }}</h2>
        </div>
        <ul class="vert-list">
          <li v-for="(cards, date) in days" :key="date" class="vert-list__item">
            <div class="listing">
              <div class="listing__identifier">
                <div class="listing__top">{{ dayHeader(date) }}</div>
                <div class="listing__bottom">{{ dateHeader(date) }}</div>
              </div>
              <div class="listing__group">
                <AvailabilityCard
                  v-for="edge in cards"
                  :key="edge.cursor"
                  :availability="edge"
                  :active="isActive(edge)"
                  @selected="availabilitySelected"
                />
              </div>
            </div>
          </li>
        </ul>
      </div>
    </li>
  </ul>
</template>

<script lang="ts">
import { parseISO, addDays, format, isBefore, isAfter } from 'date-fns';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { toDate } from 'date-fns-tz';
import AvailabilityCard from './AvailabilityCard.vue';
import { drill } from '../../lib/objectTools';
import { generateMonths, showWeekHeader, monthKey, weekKey, dayKey, monthHeader, weekHeader } from '../../lib/availability';

export default {
  name: 'AvailabilityList',
  components: {
    AvailabilityCard,
  },
  props: {
    availabilities: {
      type: Object,
      required: true,
    },
    selectedAvailability: {
      type: Object,
      default: null,
    },
  },
  data(): any {
    return {
      debouncedScroll: null,
    };
  },
  computed: {
    availabilityList(): any {
      if (this.availabilities && this.availabilities.edges) {
        let earliest = new Date();
        let latest = addDays(earliest, 30);

        // adjust earliest and latest dates if there are nodes outside of the range
        this.availabilities.edges.forEach((edge) => {
          const d = parseISO(edge.node.start);
          if (isBefore(d, earliest)) earliest = d;
          else if (isAfter(d, latest)) latest = d;
        });

        // generate new collections for each week
        const months = generateMonths(earliest, latest);

        // add the nodes to the approprate month/week
        this.availabilities.edges.forEach((edge) => {
          const d = parseISO(edge.node.start);

          const mk = monthKey(d);
          const wk = weekKey(d);
          const dk = dayKey(d);

          if (drill(months, [mk, wk, dk])) {
            months[mk][wk][dk].push(edge);
          } else {
            months[mk][wk][dk] = [edge];
          }
        });

        return months;
      }
      return {};
    },
  },
  mounted() {
    this.debouncedScroll = debounce(this.handleScroll, 100);
    window.addEventListener('scroll', this.debouncedScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.debouncedScroll);
  },
  methods: {
    showWeekHeader,
    monthHeader,
    weekHeader,
    dayHeader(day: string): string {
      return format(toDate(day), 'eee');
    },
    dateHeader(day: string): string {
      return format(toDate(day), 'd');
    },
    handleScroll() {
      if (this.$refs.vertList?.getBoundingClientRect().bottom < window.innerHeight) this.$emit('scrollEnd');
    },
    isEmpty(ob): boolean {
      return isEmpty(ob);
    },
    availabilitySelected(availability) {
      this.$emit('availabilitySelected', availability);
    },
    isActive(edge: any): boolean {
      return this.selectedAvailability && this.selectedAvailability.cursor === edge.cursor;
    },
  },
};
</script>
